import { useState } from "react";
import { Link } from "react-router-dom";
import type { CardMember } from "@/lib/types";
import { validColor } from "@/lib/color";
import { mediaUrl } from "@/lib/media";

interface MemberCardProps {
  card: CardMember;
  onOpen?: (card: CardMember) => void;
}

const VISIBLE_CAREERS = 3;

// 회장·총재 이력이면 골드 강조 대상
export function isLeadCareer(career: string): boolean {
  return career.includes("회장") || career.includes("총재");
}

// 경력 알약 배지 스타일 (골드 강조 / 일반 회색-네이비)
export function careerPillClass(career: string): string {
  return isLeadCareer(career)
    ? "bg-secondary-100 font-semibold text-secondary-900"
    : "bg-primary-50 font-medium text-primary-700";
}

function MetaCell({ label, value }: { label: string; value: string }) {
  return (
    <span className="flex flex-col text-[12px] leading-[1.45]">
      <span className="text-[11px] text-foreground-400">{label}</span>
      <span className="text-foreground-700">{value}</span>
    </span>
  );
}

// 명함 카드 1장 (뉴대전 스타일: 좌측 아바타 패널 + 경력 알약)
export default function MemberCard({ card, onOpen }: MemberCardProps) {
  const [open, setOpen] = useState(false);

  const careers = card.career ?? [];
  const visible = careers.slice(0, VISIBLE_CAREERS);
  const rest = careers.slice(VISIBLE_CAREERS);
  const highlighted = careers.length > 0;
  const clickable = typeof onOpen === "function";
  const isMine = card.is_mine === 1;

  const hp = String(card.hp ?? "").trim();
  const join = String(card.join_date ?? "").trim();
  const birth = String(card.birth ?? "").trim();
  const cate = card.cate_name || card.biz_type || "";
  const cateColor = validColor(card.cate_color) || "#17408B";
  const avatarText = (card.initial || card.name || "?").trim().slice(0, 1);
  const hasMeta = Boolean(join || birth || hp);

  return (
    <article
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={() => onOpen?.(card)}
      onKeyDown={(e) => {
        if (!clickable) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen?.(card);
        }
      }}
      className={`flex overflow-hidden rounded-[14px] border-2 bg-background-50 transition-all ${
        clickable
          ? "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300"
          : ""
      } ${
        highlighted
          ? "border-secondary-400 hover:border-secondary-500"
          : "border-background-200 hover:border-secondary-300"
      } hover:shadow-md`}
    >
      {/* 좌측 아바타 패널 */}
      <div className="flex w-[104px] shrink-0 items-center justify-center bg-primary-50">
        {card.photo ? (
          <img
            src={mediaUrl(card.photo)}
            alt={`${card.name} 회원 사진`}
            className="h-[72px] w-[72px] rounded-full object-cover object-top"
          />
        ) : (
          <span
            className="flex h-[72px] w-[72px] items-center justify-center rounded-full bg-primary-700 text-[26px] font-extrabold tracking-tight text-background-50"
            aria-hidden="true"
          >
            {avatarText}
          </span>
        )}
      </div>

      {/* 우측 정보 영역 */}
      <div className="relative min-w-0 flex-1 px-4 pb-[13px] pt-[15px]">
        <span
          className="card-wheel"
          aria-hidden="true"
          style={isMine ? { top: 46 } : undefined}
        ></span>

        {isMine && (
          <Link
            to="/my/profile"
            onClick={(e) => e.stopPropagation()}
            aria-label="내 명함 수정"
            title="내 명함 수정"
            className="absolute right-2 top-2 z-10 flex h-7 w-7 items-center justify-center rounded-full border border-primary-200 bg-primary-50 text-primary-700 transition-colors hover:bg-primary-100"
          >
            <i className="ri-edit-line text-[15px]" aria-hidden="true"></i>
          </Link>
        )}

        {/* 이름 줄 */}
        <div className="mb-2 flex flex-wrap items-baseline gap-[7px] pr-10">
          {card.aho && (
            <span className="text-[13px] text-foreground-500">{card.aho}</span>
          )}
          <span className="font-heading text-[19px] font-extrabold tracking-tight text-primary-700">
            {card.name}
          </span>
          {card.biz_position && (
            <span className="rounded bg-primary-600 px-1.5 py-0.5 text-[11px] text-background-50">
              {card.biz_position}
            </span>
          )}
        </div>

        {/* 로타리 경력 — 알약 배지 */}
        {careers.length > 0 && (
          <div className="mb-2.5">
            <div className="flex flex-col items-start gap-[5px]">
              {visible.map((c, i) => (
                <span
                  key={i}
                  className={`inline-block max-w-full whitespace-normal break-keep rounded-md px-2.5 py-1 text-[12.5px] leading-[1.35] ${careerPillClass(c)}`}
                >
                  {c}
                </span>
              ))}
            </div>

            {rest.length > 0 && (
              <div className="mt-1.5">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpen((o) => !o);
                  }}
                  className="rounded text-[12px] font-semibold text-primary-600 hover:underline"
                >
                  {open ? "− 경력 접기" : `+ 경력 ${rest.length}건 더보기`}
                </button>
                {open && (
                  <div className="mt-1.5 flex flex-col items-start gap-[5px]">
                    {rest.map((c, i) => (
                      <span
                        key={i}
                        className={`inline-block max-w-full whitespace-normal break-keep rounded-md px-2.5 py-1 text-[12.5px] leading-[1.35] ${careerPillClass(c)}`}
                      >
                        {c}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* 직장 줄 */}
        {(card.biz_name || cate) && (
          <div className="mb-2.5 flex flex-wrap items-center gap-2.5">
            {card.biz_name && (
              <span className="text-sm font-bold text-foreground-900">
                {card.biz_name}
              </span>
            )}
            {cate && (
              <span
                className="whitespace-nowrap rounded-full border px-2.5 py-[1px] text-[12px]"
                style={{ borderColor: cateColor, color: cateColor }}
              >
                {cate}
              </span>
            )}
          </div>
        )}

        {/* 하단 메타 */}
        {hasMeta && (
          <div className="flex flex-wrap items-start gap-x-4 gap-y-1 border-t border-dashed border-background-200 pt-2.5">
            {join && <MetaCell label="입회:" value={join} />}
            {birth && <MetaCell label="생일:" value={birth} />}
            {hp && (
              <a
                href={`tel:${hp.replace(/[^0-9+]/g, "")}`}
                onClick={(e) => e.stopPropagation()}
                className="ml-auto flex items-center gap-1.5 self-center whitespace-nowrap text-[13px] font-bold text-primary-600 hover:underline"
              >
                <i className="ri-phone-fill text-[13px]" aria-hidden="true"></i>
                {hp}
              </a>
            )}
          </div>
        )}
      </div>
    </article>
  );
}